
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Package } from "lucide-react";
import { format } from "date-fns";
import { useMembershipPurchases, MembershipPurchase } from "@/hooks/useMembershipPurchases";

interface PurchasesManagementProps {
  providerId: string;
}

const PurchasesManagement = ({ providerId }: PurchasesManagementProps) => {
  const { data: purchases, isLoading, error } = useMembershipPurchases(providerId);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Completed</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getMembershipTypeDisplay = (type: string) => {
    switch (type) {
      case 'pack_of_sessions':
        return 'Pack';
      case 'sessions_per_month':
        return 'Monthly';
      case 'unlimited':
        return 'Unlimited';
      default:
        return type;
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const getCustomerName = (purchase: MembershipPurchase) => {
    const profile = purchase.user_profile;
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile?.email || 'Unknown Customer';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ShoppingCart className="h-5 w-5" />
            <span>Membership Purchases</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ShoppingCart className="h-5 w-5" />
            <span>Membership Purchases</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Error loading purchases. Please try again.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <ShoppingCart className="h-5 w-5" />
          <span>Membership Purchases</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!purchases || purchases.length === 0 ? (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No purchases yet</h3>
            <p className="text-gray-500">Membership purchases will appear here once customers start buying.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Membership</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Purchase Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => (
                  <TableRow key={purchase.id}>
                    <TableCell>
                      <div className="font-medium">{getCustomerName(purchase)}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{purchase.membership?.name || 'Unknown Membership'}</div>
                      {purchase.membership?.session_count && (
                        <div className="text-sm text-gray-500">
                          {purchase.membership.session_count} sessions
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getMembershipTypeDisplay(purchase.membership?.membership_type || '')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatAmount(purchase.amount, purchase.currency)}
                      </div>
                      {purchase.coupon_discount_amount && purchase.coupon_discount_amount > 0 && (
                        <div className="text-sm text-green-600">
                          -{formatAmount(purchase.coupon_discount_amount, purchase.currency)} discount
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(purchase.status)}
                    </TableCell>
                    <TableCell>
                      {purchase.purchase_date 
                        ? format(new Date(purchase.purchase_date), 'MMM d, yyyy HH:mm')
                        : format(new Date(purchase.created_at), 'MMM d, yyyy HH:mm')
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PurchasesManagement;
